import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { AurieqLogo } from '../Brand/AurieqLogo';
import { BRAND, FOOTER_LINKS, PUBLIC_NAV } from '../../constants/branding';
import { gradientButtonSx } from '../../constants/brandStyles';

const PublicLayout: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BRAND.colors.bg,
        color: BRAND.colors.text,
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: elevated ? 'rgba(5, 7, 10, 0.88)' : 'transparent',
          backdropFilter: elevated ? 'blur(16px)' : 'none',
          borderBottom: elevated ? `1px solid ${BRAND.colors.border}` : 'none',
        }}
      >
        <Toolbar sx={{ gap: 2, py: 0.5 }}>
          <AurieqLogo variant="mark" height={32} />
          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: { xs: 0, md: 3 }, display: { xs: 'none', md: 'flex' } }}
          >
            {PUBLIC_NAV.map((item) => (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                sx={{ color: BRAND.colors.textMuted, '&:hover': { color: BRAND.colors.primary } }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          {isAuthenticated ? (
            <Button component={RouterLink} to="/dashboard" variant="contained" sx={gradientButtonSx}>
              Dashboard
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button
                component={RouterLink}
                to="/login"
                sx={{ color: BRAND.colors.textMuted, '&:hover': { color: '#fff' } }}
              >
                Sign in
              </Button>
              <Button component={RouterLink} to="/register" variant="contained" sx={gradientButtonSx}>
                Get started
              </Button>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          borderTop: `1px solid ${BRAND.colors.border}`,
          py: 6,
          mt: 4,
          background: `linear-gradient(180deg, ${BRAND.colors.bg} 0%, ${BRAND.colors.bgElevated} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          >
            <Box>
              <AurieqLogo variant="full" height={48} to="/" />
              <Typography variant="body2" sx={{ mt: 2, color: BRAND.colors.textDim, maxWidth: 320 }}>
                {BRAND.description}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={5}>
              {[
                { title: 'Product', links: FOOTER_LINKS.product },
                { title: 'Company', links: FOOTER_LINKS.company },
                { title: 'Legal', links: FOOTER_LINKS.legal },
              ].map((col) => (
                <Box key={col.title}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: BRAND.colors.text }}>
                    {col.title}
                  </Typography>
                  {col.links.map((l) => (
                    <Typography key={l.path} variant="body2" sx={{ mb: 0.75 }}>
                      <Link
                        component={RouterLink}
                        to={l.path}
                        sx={{ color: BRAND.colors.textDim, textDecoration: 'none', '&:hover': { color: BRAND.colors.primary } }}
                      >
                        {l.label}
                      </Link>
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          </Stack>
          <Divider sx={{ my: 3, borderColor: BRAND.colors.border }} />
          <Typography variant="body2" sx={{ color: BRAND.colors.textDim }}>
            © {new Date().getFullYear()} {BRAND.nameUpper}. All rights reserved. ·{' '}
            <Link href={BRAND.url} sx={{ color: BRAND.colors.textMuted, textDecoration: 'none' }}>
              {BRAND.domain}
            </Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
